import Box from '@mui/material/Box'
import MuiTable from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

interface Column {
  key: string
  header: string
  width?: string | number
}

interface TableProps {
  columns: Column[]
  data: Record<string, React.ReactNode>[]
}

export default function Table({ columns, data }: TableProps) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ my: 3, borderRadius: 2 }}
    >
      <MuiTable size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  width: col.width,
                }}
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              sx={{ '&:last-child td': { border: 0 } }}
            >
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontSize: '0.875rem' }}>
                  {row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  )
}

// Parameter table specifically for API docs
interface ParamTableProps {
  params: {
    name: string
    type: string
    required: boolean
    description: string
  }[]
}

export function ParamTable({ params }: ParamTableProps) {
  return (
    <Table
      columns={[
        { key: 'name', header: 'Parameter', width: '20%' },
        { key: 'type', header: 'Type', width: '15%' },
        { key: 'required', header: 'Required', width: '10%' },
        { key: 'description', header: 'Description' },
      ]}
      data={params.map((p) => ({
        name: (
          <Box
            component="code"
            sx={{
              bgcolor: 'grey.100',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
            }}
          >
            {p.name}
          </Box>
        ),
        type: (
          <Box
            component="span"
            sx={{ color: 'secondary.main', fontSize: '0.85rem' }}
          >
            {p.type}
          </Box>
        ),
        required: p.required ? (
          <Box component="span" sx={{ color: 'error.main', fontWeight: 500 }}>
            Yes
          </Box>
        ) : (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            No
          </Box>
        ),
        description: p.description,
      }))}
    />
  )
}
